/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ReplaySubject, forkJoin, Observable, of } from 'rxjs';
import { first } from 'rxjs/operators';
import { BankAccount, BankItem } from '@src/app/model/bankAccount';
import { Cycle } from '@src/app/model/cycle';
import { Field } from '@src/app/shared/field/Field';
import { PreferredSupplier, Supplier } from '@src/app/model/supplier';
import { Txn } from '@src/app/model/txn';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { FormComboBoxComponent } from '@src/app/shared/form/form-combo-box/form-combo-box.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { ErrorCode, FormError } from '@src/app/shared/form/form-error/form-error.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { AppPicklistControl, FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridControl } from '@src/app/shared/grid/grid-control';
import { GridField } from '@src/app/shared/grid/grid-field';
import { validateTxnDate, required } from '@src/app/shared/validators';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { CycleService } from '@src/app/modules/budget/cycle.service';
import { PeriodService } from '@src/app/modules/budget/period.service';
import { PreferredSupplierService } from '@src/app/modules/supply/preferred-supplier.service';
import { UnitService } from '@src/app/modules/unit/unit.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { BankAccountService } from '@src/app/modules/txn/bank-account.service';
import { BankOutService } from '@src/app/modules/txn/bank-out.service';
import { PicklistField } from '@src/app/shared/field/PicklistField';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { TxnService } from '@src/app/modules/txn/txn.service';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { formatCurrency } from '@angular/common';
import { Period } from '@src/app/model/period';
import { NrHelper } from '@src/app/shared/NrHelper';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { ReconciliationDetailAction } from '@src/app/modules/txn/bank-in-page/reconciliationDetailAction';
import { BankOutPageNavRoute } from '@src/app/routes/bank-out-page-nav';

export class BankOutDialogOptions {
    supplier: PreferredSupplier;
    bankItem: BankItem;
    bankAccount: BankAccount;
}

@Component({
    selector: 'app-bank-out-page',
    templateUrl: './bank-out-page.component.html',
    styleUrls: ['./bank-out-page.component.scss'],
    imports: [FormPageComponent]
})
export class BankOutPageComponent extends AbstractPageComponent {
    dataSvc = inject(BankOutService);
    bCodeSvc = inject(BCodeService);
    protected activeRoute = inject(ActivatedRoute);
    private txnService = inject(TxnService);
    protected unitSvc = inject(UnitService);
    protected cycleSvc = inject(CycleService);
    protected periodSvc = inject(PeriodService);
    protected bankAccountSvc = inject(BankAccountService);
    private currentUserSvc = inject(CurrentUserService);
    private cds = inject(ConfirmDialogService);
    dialogRef = inject<MatDialogRef<BankOutPageComponent>>(MatDialogRef, { optional: true });
    bankOutDialogOptions = inject<BankOutDialogOptions>(MAT_DIALOG_DATA, { optional: true });


    readonly path = Txn.TYPE.BANK_OUT.code

    suppliers: Supplier[] = [];

    cycleField: PicklistField = FormPicklistComponent.make('Cycle', 'txnCycleId', 'txnCycle',
        { service: this.cycleSvc }, { formColumn: 2, visible: Field.noShow }
    );
    periods: Period[] = [];

    periodField: Field = FormPicklistComponent.make('Period', 'txnPeriodId', 'txnPeriod',
        { items: this.periods },
        {
            formColumn: 2, visible: Field.noShow,
            refresh: (o: Cycle, control: AppPicklistControl) => { if (o) { control.field.setPicklistItems(o.periods); control.setValue(null); } },
        }
    );

    txnDateField: Field = FormDateComponent.make('Transaction Date', 'txnDate', {
        cellOpts: { width: '2%' }, formColumn: 2,
        validators: [validateTxnDate(this.cycleField, this.periodField)],
        valueChanges: this.dateValueChanges.bind(this)
    });

    // n.b. We need the validator, cannot disable this field...
    totalField = FormNumberComponent.make("Total", "credit",
        { format: 'currency', width: 9, formatParms: '1.2-2' },
        { formColumn: 3, readonly: true, 
            hint: $localize `Select the supplier and then enter the amounts against the outstanding approved invoices`,
            validators: [this.validateAmount.bind(this)] 
        }
    );

    invoices: Txn[];

    childTxn = new GridField({
        field:
        {
            label: Txn.TYPE.PURCHASE.name, value: 'details',
            visible: Field.formOnly, formRow: 2
        },
        rowFactory: this.childRowFactory.bind(this)
    });

    configReady = new ReplaySubject<null>(1);
    configSetup = false;

    supplierCombo = FormComboBoxComponent.make('Supplier', 'supplierId', 'supplier', {
        items:this.suppliers, refreshes: [this.refreshInvoices.bind(this)]
    }, { formColumn: 3, sendServer: false, validators: [required] });

    bankAccountField = FormPicklistComponent.make('Bank Account', 'bankAccountId', 'bankAccount',
        { service: this.bankAccountSvc, refreshes: ['bCodeId'] },
        { formColumn: 2, validators: [required] }
    );
    refNrField = FormNumberComponent.make('Reference', 'refNr', { format: 'plain' }, { 
        cellOpts: { heading: 'Ref' }, readonly: true, formColumn: 1, disable:true });
    typeField = this.txnService.getTypeField().override({disable: true});

    /* Months along the bottom of the payments charts, years as the series - see the nav route */
    monthField = Txn.makeMonthField();
    yearField = Txn.makeYearField();

    reconciledField = FormTextComponent.make('Reconciled', 'isReconciled', {
        calculateValue: (o:Txn) => o.bankItemId ? $localize `reconciled` : $localize `*** Unreconciled ***`,
        formColumn: 2,
        tableStyle: (o:Txn) => (o.bankItemId ? {} : {color:'red'}),
    })    

    config = new FormConfig({
        navRoute: BankOutPageNavRoute,
        docPath: 'omc-books/purchase-accounting/bank-payments',
        title: Txn.TYPE.BANK_OUT.name,
        help: $localize`Financial transactions`,
        readonly: true,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                this.refNrField,
                FormTextComponent.make('ledgerId', 'ledgerId', { visible: Field.noShow }),
                this.typeField,
                FieldMaker.idHolder('bankItemId'),
                this.bankAccountField,
                FormPicklistComponent.make('Account', 'bCodeId', 'bCode', { service: this.bCodeSvc },
                    {
                        formColumn: 3, validators: [required], visible: Field.noShow,
                        refresh: (o: BankAccount, control) => { control.setValue(o.bCodeId); }
                    }
                ),
                this.reconciledField,
                /* txtDateField Validator overwrites period info, need this order! */
                this.cycleField,
                this.periodField,
                this.txnDateField,
                this.monthField,
                this.yearField,
                this.supplierCombo,
                this.totalField,
                FieldMaker.notes({ formColumn: 4 }),

                this.childTxn,
            ],
            formValidator: this.validatePayment.bind(this),
            formLayout: [
                { cells: [{ width: '25%' }, { width: '25%' }, { width: '25%' }, { width: '25%' }] },
                { cells: [{ colspan: 4, width: '90%', pageTab: 'yes' }] }
            ],
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.newFactory.bind(this),
        configReady: this.configReady,
        beforeEdit: this.enhanceObject.bind(this),
        pathEdit: (o: Txn) => Txn.getTxnLink(o),
        actions: [new ReconciliationDetailAction()]
    });
    defaultBank: BankAccount;

    constructor() {
        const supplierSvc = inject(PreferredSupplierService);


        super();
        const currentUserSvc = this.currentUserSvc;


        this.subscriptions.push(forkJoin([
            currentUserSvc.getCurrentUser().pipe(first()),
            this.bankAccountSvc.get(true).pipe(first()),
            supplierSvc.get(true),
            this.currentUserSvc.getDefaultBCodes(),
            this.periodSvc.get<Period>()
        ]).subscribe(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ([currentUser, bankAccounts, suppliers, _bcodes, periods]) => {
                this.periods = periods;
                for (const acct of (bankAccounts as BankAccount[])) {
                    if (acct.defaultAcct) {
                        this.defaultBank = acct;
                    }
                }
                this.currentUser = currentUser;
                this.suppliers = suppliers as Supplier[];
                this.supplierCombo.setPicklistItems(suppliers);
                this.configReady.next(null);
                this.configSetup = true;
            }));
    }

    enhanceObject(o: Txn) {
        this.config.readonly = true;
        this.refNrField.visible = Field.showAll;
        this.typeField.visible = Field.showAll;
        this.reconciledField.visible = Field.showAll;
        return o;
    }

    childRowFactory(o: Txn) {

        const flds : Field[] = [];
        flds.push(
            FieldMaker.id(),
            FormTextComponent.make('ledgerId', 'ledgerId', { visible: Field.noShow }),
            FormTextComponent.make('typeId', 'typeId', { visible: Field.noShow }),
            FormTextComponent.make('bCodeId', 'bCodeId', { visible: Field.noShow }),

            FormTextComponent.make('txnDate', 'txnDate', { visible: Field.noShow }),
            FormTextComponent.make('txnCycleId', 'txnCycleId', { visible: Field.noShow }),
            FormTextComponent.make('txnPeriodId', 'txnPeriodId', { visible: Field.noShow }),

            FormPicklistComponent.make('Type', 'related.typeId', 'related.type',
                { items: Txn.TYPES },
                { sendServer: false, readonly: true, disable: true, cellOpts: { minWidth: "9em" } }
            ),
            FormNavLinkComponent.makeTxnLink('Ref', true),
            /*FormButtonComponent.makeLink('Ref', 'related.refNr', o ? Txn.getTxnLink(o.related) : '', {
                readonly: true, sendServer: false,
                cellOpts: { width: "6em", maxWidth: "10em" },
            }),
            */
            FieldMaker.idHolder('relatedId'),

            FormNumberComponent.make("Ref", "relatedRev", {},
                { visible: Field.noShow, cellOpts: { width: "3em", maxWidth: "3em" } }
            ),
            FormDateComponent.make('Date', 'related.txnDate',
                { sendServer: false, disable: true, cellOpts: { minWidth: "6em" } }
            ),
            FormPicklistComponent.make('Supplier', 'supplierId', null, {items: this.suppliers}, {readonly: true}),
            // Why Do we need it?
            FormTextComponent.make('Cycle', 'related.txnCycle.name',
                { cellOpts: { minWidth: "6em" }, sendServer: false, disable: true, visible: Field.noShow }
            ),
        )
        const cn = o?.related?.typeId === Txn.TYPE.PURCHASE_CN.id;
        const amtField = FormNumberComponent.make("Amount", 'related.credit',
                { format: 'currency', width: 9, formatParms: '1.2-2' },
                {
                    calculateValue: (o: Txn) => o.related.credit - o.related.debit,
                    cellOpts: { minWidth: "6em", maxWidth: "6em", style: cn ? 'color:red' : '' },
                    sendServer: false, disable: true, readonly: true
                }
            )
        flds.push(
            amtField,
            FormNumberComponent.make("Outstanding", "related.outstanding",
                { format: 'currency', width: 9, formatParms: '1.2-2' },
                { cellOpts: { minWidth: "6em", maxWidth: "6em" }, sendServer: false, disable: true, readonly: true }
            ),
            FormNumberComponent.make("Pay now", "debit",
                { format: 'currency', width: 9, formatParms: '1.2-2' },
                { valueChanges: this.totalValueChanged.bind(this), disable: !o?.related?.approvedAt  }
            ),
            FormButtonComponent.make('Pay', '', {
                name: 'action', label: '', type: 'icon', sendServer: false,
                cellOpts: { heading: '', width: '1%' },
                calculateValue: () => 'assignment_turned_in',
                toolTip: 'Click to pay this item in full',
                disable: !o?.related?.approvedAt,
                btnOpts: { clickMethod: this.allocatePayment.bind(this) }
            }),

            FieldMaker.notes({ cellOpts: { width: "50%" } }),
            FieldMaker.rev(),
            FieldMaker.deleteGridRow({ clickMethod: this.removeAllocationRow.bind(this) }),
        )

        return flds;
    }

    validateAmount(control: AppFormControl) {
        if (control?.value) {
            if (this.bankOutDialogOptions?.bankItem) {
                const amount = 0 - this.bankOutDialogOptions.bankItem.transactionAmount
                if (!NrHelper.equal(amount, control.value)) {
                    const amt = formatCurrency(0-this.bankOutDialogOptions.bankItem.transactionAmount,'EN-ie', '€');
                    return new ErrorCode('invalid', $localize `${amt} expected to match bank item`)
                }
            }
        } else {
            return new ErrorCode('required', 'You must enter an amount');
        }
        return null
    }

    dateValueChanges(): void {
        const allocations = (this.childTxn.control as GridControl).gridRows();
        const txnDate = this.txnDateField.getFormValue();
        if (allocations) {
            for (const txnRow of allocations) {
                const invDt = txnRow.get('related_txnDate').value;
                const refNr = txnRow.get('refNrLink').value;
                txnRow.get('txnDate').setValue(this.txnDateField.control.value, { emitEvent: false });
                txnRow.get('txnCycleId').setValue(this.cycleField.control.value, { emitEvent: false });
                txnRow.get('txnPeriodId').setValue(this.periodField.control.value, { emitEvent: false });
                if (invDt > txnDate) {
                    this.warnDateTooEarly(txnRow.get('debit') as AppFormControl, txnDate, invDt, refNr);
                }
            }
        }
    }

    totalValueChanged(value): void {
        let total = 0;
        const txnDate = this.txnDateField.getFormValue()
        for (const gridRow of (this.childTxn.control as GridControl).gridRows()) {
            const invDt = gridRow.get('related_txnDate').value;
            const refNr = gridRow.get('refNrLink').value;
            const invValue = gridRow.get('debit').value;
            if (invDt > txnDate && value && invValue === value) {
                this.warnDateTooEarly(gridRow.get('debit') as AppFormControl, txnDate, invDt, refNr);
            }
            const debitCtl = gridRow.get('debit');
            let amt = debitCtl.value;
            const outstanding = gridRow.get('related_outstanding').value;
            if (amt > outstanding) {
                this.cds.alert(
                    $localize `No Overpayments`, 
                    $localize `Cannot overpay invoices, amount will be reduced to ${outstanding}`)
                amt = outstanding;
                debitCtl.setValue(amt);
            }
            total += amt;
        }
        this.totalField.control.setValue(total);
    }


    refreshInvoices(supplier: PreferredSupplier) {
        this.dataSvc.getOutstanding(supplier.id).subscribe(invoices => {
            console.log(invoices);
            const openTrans = invoices; //.sort(this.txnSorter); // Txn.TYPE.BANK_IN_ON_ACCT.id
            const credits = openTrans.filter(o => o.outstanding < 0);
            for (const inv of credits) {
                this.createAllocation(inv, supplier);
            }
            const debits = openTrans.filter(o => o.outstanding > 0);
            for (const inv of debits) {
                if (!inv.approvedAt) {
                    inv.notes = 'Not approved for payment ' + (inv.notes ? inv.notes : '');
                }
                this.createAllocation(inv, supplier);
            }
        });
    }
    createAllocation(inv: Txn, supplier: PreferredSupplier) {
        console.log('Allocation ', inv, supplier);
        const newTxn = this.newAllocationTxn(supplier);
        newTxn.relatedId = inv.id;
        newTxn.relatedRev = inv.revision;
        newTxn.related = (inv as Txn);
        newTxn.notes = inv.notes;
        //const newRow: GridRow =
        this.childTxn.control.addRow(newTxn, true, false);
    }

    newAllocationTxn(supplier: PreferredSupplier): Txn {
        const newTxn = new Txn();
        newTxn.ledgerId = Txn.LEDGER.AP.id;
        newTxn.ledger = Txn.LEDGER.AP;
        newTxn.typeId = Txn.TYPE.BANK_OUT_ALLOC.id;
        newTxn.type = Txn.TYPE.BANK_OUT_ALLOC;
        newTxn.bCodeId = this.currentUserSvc.getDefaultBCode('Creditors').bCodeId;
        newTxn.txnDate = this.txnDateField.control.value;
        newTxn.txnCycleId = this.cycleField.control.value;
        newTxn.txnPeriodId = this.periodField.control.value;

        newTxn.supplierId = supplier.id;

        return newTxn;
    }

    /* Not Called
    allocatePaymentToRow(gridRow: GridRow) {
        const outstanding = gridRow.get('related_outstanding');
        const credit = gridRow.get('credit');
        const unallocated = this.page.form.formGroup.get('unallocated').value + credit.value;
        const actionBtn = (gridRow.get('action') as AppFormControl);

        if (actionBtn.value === 'backspace') {
            credit.setValue(0);
        } else {
            if (unallocated > 0 && outstanding.value > 0) {
                credit.setValue(Math.min(outstanding.value, unallocated));
            } else if (outstanding.value < 0) {
                credit.setValue(outstanding.value);
            } else if (gridRow.get('relatedId').value === null) {
                credit.setValue(unallocated);
            }
        }
    }
    */

    warnDateTooEarly(debit: AppFormControl, txnDate, invDate, refNr) {
        const tDate = new Date(txnDate).toLocaleDateString();
        const iDate = new Date(invDate).toLocaleDateString();
        const options = {
            title: $localize `Paying an Invoice Before It Was Issued?`,
            msg: this.dateTooEarlyMessage(tDate, iDate, refNr),
            warning: true,
            keepLineBreaks: true,
            options: [
                { name: $localize`Continue` },
                { name: $localize`Cancel Allocation`, action: () => {
                    debit.setValue(0.00);
                }}
            ]
        }
        this.cds.openChoice(options, false)
    }

    private dateTooEarlyMessage(paymentDate: string, invoiceDate: string, refNr: string) {
        return [
            $localize`This payment is dated ${paymentDate}, but invoice ${refNr} is dated ${invoiceDate}.`,
            $localize`Invoices are rarely paid before they are issued, so one of these may be wrong:`,
            '',
            $localize`• The wrong invoice - is there a later invoice this payment was for?`,
            $localize`• The invoice date - you can edit the invoice to correct it.`,
            $localize`• The payment date - check the bank date entered for this payment.`,
            '',            
            $localize`Dates matter because they decides which financial period/year transaction falls into.`,
            $localize`If the invoice and both dates are correct, click Continue.`,
            $localize`Click Cancel to remove the amount allocated to this invoice.`,
        ].join('\n');
    }

    allocatePayment(ctl: AppFormControl): void {
        const gridRow = ctl.getRow()

        const outstanding = gridRow.get('related_outstanding');
        const debit = gridRow.get('debit') as AppFormControl;
        const actionBtn = (gridRow.get('action') as AppFormControl);

        console.log({ outstanding, debit, actionBtn });
        if (actionBtn.value === 'backspace') {
            debit.setValue(0);
            actionBtn.setValue('assignment_turned_in');
        } else {
            if (this.bankOutDialogOptions?.bankItem) {
                const amount = 0 - this.bankOutDialogOptions.bankItem.transactionAmount
                debit.setValue(Math.min(outstanding.value, amount));
            } else {
                debit.setValue(outstanding.value);
            }            
            
            debit.markAsDirty(); debit.markAsTouched();
            actionBtn.setValue('backspace');
        }
    }

    removeAllocationRow(ctl: AppFormControl): void {
        if (ctl.isGridCell) {
            ctl.getRow().delete();
            this.totalValueChanged(null);
        }
    }
    newFactory(): Observable<Txn> {
        const txn = new Txn();
        txn.ledgerId = Txn.LEDGER.AP.id;
        txn.ledger = Txn.LEDGER.AP;
//        this.refNrField.visible = Field.noShow;
//        this.typeField.visible = Field.noShow;        

        txn.typeId = Txn.TYPE.BANK_OUT.id;
        txn.type = Txn.TYPE.BANK_OUT;

        if (this.defaultBank) {
            txn.bankAccountId = this.defaultBank.id;
            txn.bankAccount = this.defaultBank;

            txn.bCodeId = this.defaultBank.bCodeId;
        }

        //txn.txnDate = moment().toISOString().substring(0, 10);
        //validateTxnDate(this.cycleField, this.periodField)(this.)

        this.config.readonly = false;
        this.reconciledField.visible = Field.noShow;
        if (this.bankOutDialogOptions?.bankItem) {
            txn.bankItemId = this.bankOutDialogOptions.bankItem.id;
            txn.txnDate = this.bankOutDialogOptions.bankItem.bookingDate;
            const prd = this.periodSvc.getOpenPeriod(txn.txnDate, this.periods)
            if (prd) {
                txn.txnPeriodId = prd.id;
                txn.txnCycleId = prd.cycleId;
            }
            this.txnDateField.disableControl();
        }
        if (this.bankOutDialogOptions?.bankAccount) {
            txn.bankAccountId = this.bankOutDialogOptions.bankAccount.id;
            txn.bankAccount = this.bankOutDialogOptions.bankAccount;
            txn.bCodeId = this.bankOutDialogOptions.bankAccount.bCodeId;
            this.bankAccountField.disableControl();
        }

        if (this.bankOutDialogOptions?.supplier) {
            txn.supplierId = this.bankOutDialogOptions.supplier.id;
            this.refreshInvoices(this.bankOutDialogOptions.supplier);
        }

        return of(txn);
    }

    validatePayment() {

        if (this.totalField?.control?.value === 0) {
            return FormError.reportError('paymentLineItems', $localize`Cannot record a payment of zero!`);
        }

        return null;
    }
}
